<template>
  <Teleport to="body">
    <canvas v-if="bursting" ref="canvas" class="confetti" aria-hidden="true"></canvas>
    <Transition name="celebration">
      <div v-if="reward" class="celebration-backdrop" @click.self="close">
        <div class="celebration" role="status">
          <!-- 火焰先燃起来，火星往上飘，然后连续天数从昨天的数翻到今天 -->
          <div class="flame-stage" aria-hidden="true">
            <span class="flame-glow"></span>
            <StreakFlame class="flame" lit ignite />
            <span v-for="(ember, index) in EMBERS" :key="index" class="ember" :style="ember"></span>
          </div>
          <div class="streak-roll" :aria-label="`${reward.streak} ${t('checkIn.celebrationStreak')}`">
            <span class="roll-old" aria-hidden="true">{{ reward.streak - 1 }}</span>
            <span class="roll-new" aria-hidden="true">{{ reward.streak }}</span>
          </div>
          <span class="streak-unit">{{ t('checkIn.celebrationStreak') }}</span>
          <strong class="celebration-title">{{ reward.bonus ? t('checkIn.celebrationMilestone', { streak: reward.streak }) : t('checkIn.celebrationTitle') }}</strong>
          <p class="celebration-reward">{{ t('checkIn.celebrationReward', { amount: reward.daily + reward.bonus }) }}</p>
          <p v-if="reward.freezesUsed" class="celebration-freeze">
            <span class="material-icons" aria-hidden="true">ac_unit</span>{{ t('checkIn.celebrationFreezeUsed', { count: reward.freezesUsed }) }}
          </p>
          <p v-if="reward.freezeEarned" class="celebration-freeze">
            <span class="material-icons" aria-hidden="true">ac_unit</span>{{ t('checkIn.celebrationFreezeEarned') }}
          </p>
          <button ref="closeButton" type="button" class="celebration-continue" @click="close">{{ t('checkIn.celebrationContinue') }}</button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { nextTick, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import StreakFlame from './StreakFlame.vue'
import { checkInReward } from '@/utils/listeningActivity'

// 听满当天打卡时长：屏幕中间燃起火焰、翻出新的连续天数，两侧再喷一阵礼花；点继续或空白处关闭，10 秒后自动收起
const COLORS = ['#31C7B3', '#087D72', '#FFD166', '#FF8A1F', '#9BE3D8']
const PIECES = 160
const DURATION = 3200
const GRAVITY = 0.32
const VISIBLE_MS = 10000
// 火星的水平位置、延迟和大小
const EMBERS = [[-26, 0, 5], [18, 0.35, 4], [-8, 0.7, 6], [30, 1.05, 4], [-34, 1.4, 4], [6, 1.75, 5], [24, 2.1, 6], [-18, 2.45, 4]]
  .map(([x, delay, size]) => ({ '--x': `${x}px`, animationDelay: `${delay}s`, width: `${size}px`, height: `${size}px` }))

const { t } = useI18n()
const reward = ref(null)
const bursting = ref(false)
const canvas = ref(null)
const closeButton = ref(null)
let frame = null
let hideTimer = null
let burstTimer = null

function launchPieces(width, height) {
  return Array.from({ length: PIECES }, (_, index) => {
    const fromLeft = index % 2 === 0
    const angle = (fromLeft ? -62 : -118) + (Math.random() - 0.5) * 36
    // 按窗口高度算初速度，最高的纸片能飞到屏幕上部
    const speed = Math.sqrt(2 * GRAVITY * height * (0.5 + Math.random() * 0.5)) * 1.1
    return {
      x: fromLeft ? width * 0.08 : width * 0.92,
      y: height * 0.95,
      vx: Math.cos(angle * Math.PI / 180) * speed,
      vy: Math.sin(angle * Math.PI / 180) * speed,
      size: 6 + Math.random() * 6,
      rotation: Math.random() * Math.PI,
      spin: (Math.random() - 0.5) * 0.3,
      color: COLORS[index % COLORS.length]
    }
  })
}

async function burst() {
  cancelAnimationFrame(frame)
  bursting.value = true
  await nextTick()
  const element = canvas.value
  const ratio = window.devicePixelRatio || 1
  const width = window.innerWidth
  const height = window.innerHeight
  element.width = width * ratio
  element.height = height * ratio
  const context = element.getContext('2d')
  context.scale(ratio, ratio)
  const pieces = launchPieces(width, height)
  const start = performance.now()
  let last = start

  const draw = now => {
    const elapsed = now - start
    const step = Math.min(2, (now - last) / 16.7)
    last = now
    context.clearRect(0, 0, width, height)
    context.globalAlpha = Math.max(0, 1 - Math.max(0, elapsed - DURATION * 0.6) / (DURATION * 0.4))
    for (const piece of pieces) {
      piece.vx *= 0.985 ** step
      piece.vy = piece.vy * 0.985 ** step + GRAVITY * step
      piece.x += piece.vx * step
      piece.y += piece.vy * step
      piece.rotation += piece.spin * step
      context.save()
      context.translate(piece.x, piece.y)
      context.rotate(piece.rotation)
      context.fillStyle = piece.color
      // 纸片翻转：宽度随角度变化
      context.fillRect(-piece.size / 2, -piece.size / 4, piece.size * Math.abs(Math.cos(piece.rotation * 2)), piece.size / 2)
      context.restore()
    }
    if (elapsed < DURATION) frame = requestAnimationFrame(draw)
    else bursting.value = false
  }
  frame = requestAnimationFrame(draw)
}

function close() {
  reward.value = null
  clearTimeout(hideTimer)
}

function onKey(event) {
  if (event.key === 'Escape') close()
}

async function celebrate(next) {
  reward.value = next
  clearTimeout(hideTimer)
  clearTimeout(burstTimer)
  hideTimer = setTimeout(close, VISIBLE_MS)
  await nextTick()
  closeButton.value?.focus({ preventScroll: true })
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return
  // 等火焰燃起来再放礼花
  burstTimer = setTimeout(burst, 450)
}

watch(checkInReward, celebrate)
watch(reward, value => {
  if (value) window.addEventListener('keydown', onKey)
  else window.removeEventListener('keydown', onKey)
})
onUnmounted(() => {
  cancelAnimationFrame(frame)
  clearTimeout(hideTimer)
  clearTimeout(burstTimer)
  window.removeEventListener('keydown', onKey)
})
defineExpose({ celebrate })
</script>

<style scoped>
.confetti {
  position: fixed;
  inset: 0;
  z-index: 3002;
  width: 100vw;
  height: 100vh;
  pointer-events: none;
}

.celebration-backdrop {
  position: fixed;
  inset: 0;
  z-index: 3001;
  display: grid;
  place-items: center;
  padding: var(--space-4);
  background: var(--color-overlay);
  backdrop-filter: blur(4px);
}

.celebration {
  display: grid;
  justify-items: center;
  width: min(360px, 100%);
  padding: var(--space-8) var(--space-6) var(--space-6);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
  box-shadow: var(--shadow-float);
  text-align: center;
}

.flame-stage {
  position: relative;
  display: grid;
  place-items: center;
  width: 140px;
  height: 140px;
}

.flame-glow {
  position: absolute;
  inset: 8px;
  border-radius: 50%;
  background: var(--streak-flame-glow);
  animation: glow 2s ease-in-out 0.5s infinite alternate both;
}

.flame {
  position: relative;
  width: 84px;
  height: 105px;
}

.ember {
  position: absolute;
  bottom: 36px;
  left: calc(50% + var(--x));
  border-radius: 50%;
  background: var(--streak-flame-outer);
  opacity: 0;
  animation: ember 2.8s ease-out infinite;
}

.streak-roll {
  position: relative;
  height: 3.75rem;
  margin-top: var(--space-2);
  overflow: hidden;
  font-family: var(--font-family-data);
  font-size: 3.5rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 3.75rem;
  letter-spacing: -0.04em;
}

.roll-old,
.roll-new {
  display: block;
  animation: roll 500ms var(--ease-spring) 700ms both;
}

.roll-new {
  color: var(--streak-flame-outer);
}

.streak-unit {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.celebration-title {
  margin-top: var(--space-5);
  color: var(--ink-primary);
  font-size: var(--font-size-lg);
  font-weight: 600;
  line-height: 1.4;
}

.celebration-reward,
.celebration-freeze {
  margin: var(--space-1) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

.celebration-freeze {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  color: var(--streak-freeze);
}

.celebration-freeze .material-icons {
  font-size: 16px;
}

.celebration-continue {
  width: 100%;
  height: 44px;
  margin-top: var(--space-6);
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
  font: inherit;
  font-size: var(--font-size-sm);
  font-weight: 600;
  cursor: pointer;
}

.celebration-continue:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

@keyframes glow {
  from { transform: scale(0.85); opacity: 0.6; }
  to { transform: scale(1.05); opacity: 1; }
}

@keyframes ember {
  0% { opacity: 0; transform: translate(0, 0) scale(1); }
  15% { opacity: 1; }
  100% { opacity: 0; transform: translate(calc(var(--x) * -0.4), -96px) scale(0.4); }
}

/* 两个数字上下叠着，一起上移一格：旧数字滑走，新数字顶上来 */
@keyframes roll {
  from { transform: translateY(0); }
  to { transform: translateY(-100%); }
}

.celebration-enter-active,
.celebration-leave-active {
  transition: opacity 0.3s ease;
}

.celebration-enter-active .celebration {
  transition: transform 0.45s var(--ease-spring);
}

.celebration-enter-from,
.celebration-leave-to {
  opacity: 0;
}

.celebration-enter-from .celebration {
  transform: scale(0.92) translateY(12px);
}

@media (prefers-reduced-motion: reduce) {
  .flame-glow,
  .ember,
  .roll-old,
  .roll-new {
    animation: none;
  }

  .roll-old {
    display: none;
  }

  .celebration-enter-active,
  .celebration-leave-active,
  .celebration-enter-active .celebration {
    transition: none;
  }
}
</style>
